import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    CrewInviteLink,
    CreateCrewInviteLinkDto,
    UpdateCrewInviteLinkDto,
    UseInviteLinkDto,
    ApiResponse
} from '../../../shared/types';
import axiosService from '../services/axios.service';

// Query key factory
export const crewInviteLinkKeys = {
    all: ['crew-invite-links'] as const,
    crews: (crewSlug: string) => [...crewInviteLinkKeys.all, 'crew', crewSlug] as const,
    list: (crewSlug: string) => [...crewInviteLinkKeys.crews(crewSlug), 'list'] as const,
    detail: (linkId: string) => [...crewInviteLinkKeys.all, 'detail', linkId] as const,
    verify: (code: string) => [...crewInviteLinkKeys.all, 'verify', code] as const
};

// API functions
const crewInviteLinkApi = {
    // Admin operations (require crew admin permissions)
    getCrewInviteLinks: async (crewSlug: string): Promise<ApiResponse<CrewInviteLink[]>> => {
        const { data } = await axiosService.get<ApiResponse<CrewInviteLink[]>>(`/c/${crewSlug}/invite-links`);
        return data;
    },

    createInviteLink: async (crewSlug: string, linkData: CreateCrewInviteLinkDto): Promise<ApiResponse<CrewInviteLink>> => {
        const { data } = await axiosService.post<ApiResponse<CrewInviteLink>>(`/c/${crewSlug}/invite-links`, linkData);
        return data;
    },

    updateInviteLink: async (crewSlug: string, linkId: string, linkData: UpdateCrewInviteLinkDto): Promise<ApiResponse<CrewInviteLink>> => {
        const { data } = await axiosService.put<ApiResponse<CrewInviteLink>>(`/c/${crewSlug}/invite-links/${linkId}`, linkData);
        return data;
    },

    deactivateInviteLink: async (crewSlug: string, linkId: string): Promise<ApiResponse<void>> => {
        const { data } = await axiosService.put<ApiResponse<void>>(`/c/${crewSlug}/invite-links/${linkId}/deactivate`);
        return data;
    },

    deleteInviteLink: async (crewSlug: string, linkId: string): Promise<ApiResponse<void>> => {
        const { data } = await axiosService.delete<ApiResponse<void>>(`/c/${crewSlug}/invite-links/${linkId}`);
        return data;
    },

    getInviteLinkStats: async (crewSlug: string, linkId: string): Promise<ApiResponse<any>> => {
        const { data } = await axiosService.get<ApiResponse<any>>(`/c/${crewSlug}/invite-links/${linkId}/stats`);
        return data;
    },

    // Public operations (no auth or basic auth required)
    verifyInviteLink: async (code: string): Promise<ApiResponse<any>> => {
        const { data } = await axiosService.get<ApiResponse<any>>(`/invite-links/verify/${code}`);
        return data;
    },

    useInviteLink: async (linkData: UseInviteLinkDto): Promise<ApiResponse<any>> => {
        const { data } = await axiosService.post<ApiResponse<any>>(`/invite-links/use`, linkData);
        return data;
    }
};

// React Query hooks for admin operations
export const useCrewInviteLinks = (crewSlug: string) => {
    return useQuery({
        queryKey: crewInviteLinkKeys.list(crewSlug),
        queryFn: () => crewInviteLinkApi.getCrewInviteLinks(crewSlug),
        enabled: !!crewSlug
    });
};

export const useInviteLinkStats = (crewSlug: string, linkId: string) => {
    return useQuery({
        queryKey: crewInviteLinkKeys.detail(linkId),
        queryFn: () => crewInviteLinkApi.getInviteLinkStats(crewSlug, linkId),
        enabled: !!crewSlug && !!linkId
    });
};

export const useCreateInviteLink = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (linkData: CreateCrewInviteLinkDto) => crewInviteLinkApi.createInviteLink(crewSlug, linkData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: crewInviteLinkKeys.crews(crewSlug) });
        }
    });
};

export const useUpdateInviteLink = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ linkId, data }: { linkId: string; data: UpdateCrewInviteLinkDto }) =>
            crewInviteLinkApi.updateInviteLink(crewSlug, linkId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: crewInviteLinkKeys.crews(crewSlug) });
        }
    });
};

export const useDeactivateInviteLink = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (linkId: string) => crewInviteLinkApi.deactivateInviteLink(crewSlug, linkId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: crewInviteLinkKeys.crews(crewSlug) });
        }
    });
};

export const useDeleteInviteLink = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (linkId: string) => crewInviteLinkApi.deleteInviteLink(crewSlug, linkId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: crewInviteLinkKeys.crews(crewSlug) });
        }
    });
};

// React Query hooks for public operations
export const useVerifyInviteLink = (code: string) => {
    return useQuery({
        queryKey: crewInviteLinkKeys.verify(code),
        queryFn: () => crewInviteLinkApi.verifyInviteLink(code),
        enabled: !!code,
        retry: false // Don't retry on 404
    });
};

export const useUseInviteLink = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (linkData: UseInviteLinkDto) => crewInviteLinkApi.useInviteLink(linkData),
        onSuccess: () => {
            // Invalidate crews list since user joined a new crew
            queryClient.invalidateQueries({ queryKey: ['crews'] });
        }
    });
};
